import React, { useEffect, useMemo, useRef, useState } from "react"
import Cropper, { Area, MediaSize } from "react-easy-crop"
import "react-easy-crop/react-easy-crop.css"
import { createCroppedImage } from "react-image-crop-pro"
import { isTiff, isHeic, toBrowserBlob } from "../../file"
import Button from "../Button"
import SkeletonLoader from "../SkeletonLoader"
import ColumnContainer from "../Layout/ColumnContainer"
import Label from "../Label"
import Typography, { typographyTypes } from "../Typography"
import RowContainer from "../Layout/RowContainer"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import { populateCmsTemplate } from "../../utils"

export interface ImageCropPanelProps {
  file: File
  onCropSet: (croppedFile: File) => void
  onCropReadyChange?: (isReady: boolean) => void
  isProcessing?: boolean
}

const DEFAULT_CROP = { x: 0, y: 0 }
const DEFAULT_ZOOM = 1
const DEFAULT_MAX_ZOOM = 3
const MIN_CROP_PX = 134

/**
 * Interactive crop panel wrapping `react-easy-crop` with a circular crop shape,
 * dynamic max-zoom capped to keep output >= MIN_CROP_PX, an accessible zoom
 * slider, and Reset / Set Crop controls that appear only when there are
 * uncommitted changes.
 *
 * @param file - The source image File selected by the user.
 * @param onCropSet - Called with the cropped File after the user confirms via "Set Crop".
 * @param onCropReadyChange - Optional - Called with `true` when crop and zoom match the last committed
 *   state (no pending changes), and `false` when uncommitted changes exist. Used by the
 *   parent to gate the upload button.
 * @param isProcessing - Optional, default true - Disables all controls while the parent is processing an upload.
 */
export const ImageCropPanel: React.FC<ImageCropPanelProps> = ({
  file,
  onCropSet,
  onCropReadyChange,
  isProcessing = false,
}) => {
  const {
    cms: { common: commonText },
  } = useCMSReady(["common"])
  const [imageSrc, setImageSrc] = useState("")
  const [crop, setCrop] = useState(DEFAULT_CROP)
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [committedCrop, setCommittedCrop] = useState(DEFAULT_CROP)
  const [committedZoom, setCommittedZoom] = useState(DEFAULT_ZOOM)
  const [isSettingCrop, setIsSettingCrop] = useState(false)
  const [showCropControlButtons, setShowCropControlButtons] = useState(false)
  const [cropStatusMessage, setCropStatusMessage] = useState("")
  const [mediaSize, setMediaSize] = useState<MediaSize | null>(null)
  const [isCropperFocused, setIsCropperFocused] = useState(false)
  const [isConverting, setIsConverting] = useState(true)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const zoomRafRef = useRef<number | null>(null)

  // Cap ensures output stays >= MIN_CROP_PX on each axis
  const maxZoom = useMemo(() => {
    if (!mediaSize) return DEFAULT_MAX_ZOOM
    const computed =
      Math.min(mediaSize.naturalWidth, mediaSize.naturalHeight) / MIN_CROP_PX
    return Math.max(1, computed)
  }, [mediaSize])

  // Clamp zoom only when maxZoom itself changes (e.g. after media loads).
  // Using the functional updater avoids adding `zoom` to the deps, which
  // would re-run this effect on every user interaction and risk a loop.
  useEffect(() => {
    setZoom((prev) => Math.min(prev, maxZoom))
  }, [maxZoom])

  // Move focus to the panel heading on mount so screen reader users are oriented
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  // Derived output MIME: TIFF/HEIC are converted to PNG for display, so the
  // crop output must also be PNG — canvas can't encode those formats natively.
  const outputMimeType = isTiff(file) || isHeic(file) ? "image/png" : file.type

  useEffect(() => {
    let url = ""
    let cancelled = false
    setIsConverting(true)

    toBrowserBlob(file).then((blob) => {
      const objectUrl = URL.createObjectURL(blob)
      if (cancelled) {
        URL.revokeObjectURL(objectUrl)
        return
      }
      url = objectUrl
      setImageSrc(url)
      setIsConverting(false)
      setCrop(DEFAULT_CROP)
      setZoom(DEFAULT_ZOOM)
      setCommittedCrop(DEFAULT_CROP)
      setCommittedZoom(DEFAULT_ZOOM)
      setCropStatusMessage("")
      setMediaSize(null)
    })

    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [file])

  useEffect(() => {
    const cropUnchanged =
      crop.x === committedCrop.x && crop.y === committedCrop.y
    const zoomUnchanged = zoom === committedZoom
    const cropComplete = cropUnchanged && zoomUnchanged

    onCropReadyChange?.(cropComplete)
    setShowCropControlButtons(!cropComplete)
  }, [committedCrop, committedZoom, crop, onCropReadyChange, zoom])

  const handleCropComplete = (_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels)
  }

  const handleMediaLoaded = (size: MediaSize) => {
    setMediaSize(size)
  }

  const handleSetCrop = async () => {
    if (!croppedAreaPixels || !imageSrc) return
    setIsSettingCrop(true)
    try {
      const result = await createCroppedImage(imageSrc, croppedAreaPixels, 0, {
        format: "file",
        // The library's TS signature narrows `type` to "image/jpeg"; the cast
        // satisfies the type checker while passing the correct MIME at runtime.
        // TIFF/HEIC use "image/png" (their converted display format) to avoid
        // a JPEG encode that would fill transparent pixels with black.
        type: outputMimeType as "image/jpeg",
        fileName: file.name,
      })
      if (result.file) {
        onCropSet(result.file)
        setCommittedCrop(crop)
        setCommittedZoom(zoom)
        const w = Math.round(croppedAreaPixels.width)
        const h = Math.round(croppedAreaPixels.height)
        setCropStatusMessage(
          populateCmsTemplate(
            [w.toString(), h.toString()],
            "cms.common.imageCropAreaSetStatusTemplate",
            commonText?.imageCropAreaSetStatusTemplate ??
              "Crop area set: {0} by {1} pixels.",
          ),
        )
      }
    } finally {
      setIsSettingCrop(false)
    }
  }

  const handleReset = () => {
    setCrop(committedCrop)
    setZoom(committedZoom)
  }

  /**
   * Throttles zoom slider input to one update per animation frame (~16 ms at 60 fps).
   *
   * Without this, dragging the slider quickly fires dozens of onChange events per
   * frame. Each setZoom call triggers react-easy-crop's componentDidUpdate →
   * recomputeCropPosition → onCropChange cascade, which queues another setState
   * before the previous render has settled. React hits its 50-update guard and
   * throws "Maximum update depth exceeded."
   *
   * By coalescing all intra-frame events into one setZoom call we keep the slider
   * visually smooth while ensuring the Cropper never receives updates faster than
   * it can process them.
   */
  const handleZoomSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value)
    if (zoomRafRef.current !== null) cancelAnimationFrame(zoomRafRef.current)
    zoomRafRef.current = requestAnimationFrame(() => {
      zoomRafRef.current = null
      setZoom(val)
    })
  }

  const isBusy = isSettingCrop || isProcessing
  const cropW = croppedAreaPixels ? Math.round(croppedAreaPixels.width) : null
  const cropH = croppedAreaPixels ? Math.round(croppedAreaPixels.height) : null

  if (isConverting) {
    return (
      <ColumnContainer styleOverrides="space-y-4">
        <SkeletonLoader height={240} />
        <SkeletonLoader height={12} />
      </ColumnContainer>
    )
  }

  if (!imageSrc) return null

  return (
    <ColumnContainer styleOverrides="space-y-4">
      {/* Visually hidden heading: doubles as focus target on panel mount (2.4.3, 1.3.1) */}
      <h3 ref={headingRef} tabIndex={-1} className="sr-only focus:outline-none">
        {commonText?.imageCropHeadingText ??
          "Adjust photo crop. Use arrow keys to pan the image. Use the zoom slider to zoom in or out."}
      </h3>
      {/* role="group" gives the cropper area a labelled boundary (4.1.2) */}
      <div
        role="group"
        aria-label={commonText?.imageCropAreaAriaLabel ?? "Crop area"}
        className="relative w-full h-[240px] rounded-md overflow-hidden bg-black"
        onFocus={() => setIsCropperFocused(true)}
        onBlur={() => setIsCropperFocused(false)}
      >
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="round"
          maxZoom={maxZoom}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={handleCropComplete}
          onMediaLoaded={handleMediaLoaded}
          style={{
            cropAreaStyle: {
              outline: isCropperFocused ? "2px solid white" : "none",
              border: `2px solid ${isCropperFocused ? "#007DB3" : "rgba(255, 255, 255, 0.75)"}`,
              ...(isCropperFocused && {
                boxShadow:
                  "inset 0 0 0 2px white, 0 0 0 9999em rgba(0, 0, 0, 0.5)",
              }),
            },
          }}
        />
      </div>
      {/* Live region announces crop confirmation to screen readers (4.1.3) */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {cropStatusMessage}
      </div>
      <ColumnContainer styleOverrides="space-y-1">
        <RowContainer styleOverrides="items-center space-x-2">
          <Label
            htmlFor="flex flex-row photo-crop-zoom"
            text={commonText?.imageCropZoomLabel ?? "Zoom"}
            size="sm"
            fontWeight="medium"
          />
          {cropW !== null && cropH !== null && (
            <Typography
              styleOverrides="ml-1"
              variant={typographyTypes.HELPER}
              text={`(${cropW} px × ${cropH} px)`}
            />
          )}
        </RowContainer>
        <input
          id="photo-crop-zoom"
          type="range"
          min={1}
          max={maxZoom}
          step={0.01}
          value={zoom}
          onChange={handleZoomSliderChange}
          aria-label={commonText?.imageCropZoomLabel ?? "Zoom"}
          aria-valuemin={1}
          aria-valuemax={maxZoom}
          aria-valuenow={zoom}
          aria-valuetext={
            cropW !== null && cropH !== null
              ? `${zoom.toFixed(1)}x, ${cropW} by ${cropH} pixels`
              : `${zoom.toFixed(1)}x`
          }
          className="w-full accent-[#007DB3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-blue focus-visible:ring-offset-2 rounded-sm"
          disabled={isBusy}
        />
      </ColumnContainer>
      <div className="flex gap-3 justify-end">
        {showCropControlButtons && (
          <>
            <Button
              buttonLabel={commonText?.imageCropResetButtonLabel ?? "Reset"}
              type="tertiary"
              buttonSize="sm"
              roundedFull={false}
              onClick={handleReset}
              disabled={isBusy}
              ariaLabel={
                commonText?.imageCropResetAriaLabel ?? "Reset photo crop"
              }
            />
            <Button
              buttonLabel={
                commonText?.imageCropSetCropButtonLabel ?? "Set Crop"
              }
              type="primary"
              buttonSize="sm"
              roundedFull={false}
              onClick={handleSetCrop}
              disabled={!croppedAreaPixels || isBusy}
              loading={isSettingCrop}
              ariaLabel={
                commonText?.imageCropSetCropAriaLabel ?? "Set cropped photo"
              }
            />
          </>
        )}
      </div>
    </ColumnContainer>
  )
}
